import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import NotFound from './NotFound';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaClock, 
  FaDollarSign, 
  FaHeart, 
  FaBriefcase, 
  FaGraduationCap, 
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaIndustry,
  FaQuestionCircle,
  FaLaptopHouse
} from 'react-icons/fa';
// Import Firebase functions
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isProfileComplete } from '../utils/formValidation';

const EMPLOYMENT_TYPE_MAP = {
  'FULL_TIME': 'FULL_TIME',
  'FULLTIME': 'FULL_TIME',
  'FULL-TIME': 'FULL_TIME',
  'PART_TIME': 'PART_TIME',
  'PARTTIME': 'PART_TIME',
  'PART-TIME': 'PART_TIME',
  'CONTRACT': 'CONTRACTOR',
  'CONTRACTOR': 'CONTRACTOR',
  'CONTRACT/TEMPORARY': 'CONTRACTOR',
  'TEMPORARY': 'TEMPORARY',
  'INTERNSHIP': 'INTERN',
  'INTERN': 'INTERN',
  'VOLUNTEER': 'VOLUNTEER',
  'PER_DIEM': 'PER_DIEM',
};

const toSchemaEmploymentType = (type) => {
  if (!type) return ['OTHER'];
  const key = type.toUpperCase().trim();
  return [EMPLOYMENT_TYPE_MAP[key] || 'OTHER'];
};

const JobDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [isCandidate, setIsCandidate] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const [isExpiredJob, setIsExpiredJob] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const auth = getAuth();
  const db = getDatabase();
  const functions = getFunctions();

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        let foundJob = null;
        let foundEmployerId = null;
        let foundJobId = null;

        // 1. Fast path: slug index lookup
        const slugSnap = await get(ref(db, `jobSlugs/${slug}`));
        if (slugSnap.exists()) {
          const { employerId, jobId } = slugSnap.val();
          const jobSnap = await get(ref(db, `jobs/${employerId}/${jobId}`));
          if (jobSnap.exists()) {
            foundJob = jobSnap.val();
            foundEmployerId = employerId;
            foundJobId = jobId;
          } else {
            // Job may have expired — check expired-jobs
            const expSnap = await get(ref(db, `expired-jobs/${employerId}/${jobId}`));
            if (expSnap.exists()) {
              foundJob = expSnap.val();
              foundEmployerId = employerId;
              foundJobId = jobId;
              setIsExpiredJob(true);
            }
          }
        }

        // 2. Fallback: scan all jobs (supports old Firebase-ID URLs)
        if (!foundJob) {
          const jobsSnapshot = await get(ref(db, 'jobs'));
          if (jobsSnapshot.exists()) {
            jobsSnapshot.forEach((employerSnapshot) => {
              const employerJobs = employerSnapshot.val();
              if (employerJobs[slug]) {
                foundJob = employerJobs[slug];
                foundEmployerId = employerSnapshot.key;
                foundJobId = slug;
              }
            });
          }
        }

        // 3. Fallback: scan expired-jobs for old ID URLs
        if (!foundJob) {
          const expiredJobsSnapshot = await get(ref(db, 'expired-jobs'));
          if (expiredJobsSnapshot.exists()) {
            expiredJobsSnapshot.forEach((employerSnapshot) => {
              const employerJobs = employerSnapshot.val();
              if (employerJobs[slug]) {
                foundJob = employerJobs[slug];
                foundEmployerId = employerSnapshot.key;
                foundJobId = slug;
              }
            });
          }
          if (foundJob) setIsExpiredJob(true);
        }

        if (foundJob) {
          // Fetch employer details
          const employerRef = ref(db, `employers/${foundEmployerId}/profile`);
          const employerSnapshot = await get(employerRef);
          const employerData = employerSnapshot.exists() ? employerSnapshot.val() : null;

          // Combine job and employer data
          const fullJobData = {
            ...foundJob,
            id: foundJobId,
            slug,
            employerId: foundEmployerId,
            companyName: employerData?.companyName || foundJob.companyName || 'Company Name',
            employer: employerData || null
          };

          setJob(fullJobData);

          // Initialize answers state based on questions
          if (fullJobData.questions) {
            const initialAnswers = {};
            fullJobData.questions.forEach(q => {
              initialAnswers[q.id] = q.type === 'multiple_choice' ? '' : '';
            });
            setAnswers(initialAnswers);
          }

          // Check user status if logged in
          if (auth.currentUser) {
            const userId = auth.currentUser.uid;

            // Check if user is a candidate
            const candidateRef = ref(db, `candidates/${userId}`);
            const candidateSnapshot = await get(candidateRef);

            if (candidateSnapshot.exists()) {
              setIsCandidate(true);
              setCandidateData(candidateSnapshot.val());
            }

            // Check application and favorite status
            const [favSnapshot, appSnapshot] = await Promise.all([
              get(ref(db, `candidates/${userId}/favorites/${foundJobId}`)),
              get(ref(db, `candidates/${userId}/applications/${foundJobId}`))
            ]);

            setIsFavorite(favSnapshot.exists());
            setHasApplied(appSnapshot.exists());
          }
        } else {
          setError('Job not found');
        }
      } catch (err) {
        console.error('Error fetching job details:', err);
        setError('Failed to load job details');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [slug, db, auth.currentUser]);

  useEffect(() => {
    if (!job) return;

    const datePosted = new Date(job.approvedAt || job.createdAt).toISOString();
    const expiryTs = job.expirationDate || job.expiryDate;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.jobTitle,
      description: `<p>${(job.description || '').replace(/\n/g, '<br>')}</p>`,
      datePosted,
      ...(expiryTs && { validThrough: new Date(expiryTs).toISOString() }),
      employmentType: toSchemaEmploymentType(job.employmentType),
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.parish || 'Jamaica',
          addressRegion: job.parish || 'Jamaica',
          addressCountry: 'JM',
        },
      },
      ...(job.workType?.toLowerCase().includes('remote') && {
        jobLocationType: 'TELECOMMUTE',
      }),
      hiringOrganization: {
        '@type': 'Organization',
        name: job.companyName,
        ...(job.website && { sameAs: job.website }),
        ...(job.contactEmail && { email: job.contactEmail }),
        ...(job.contactPhone && { telephone: job.contactPhone }),
      },
      directApply: true,
      url: window.location.href,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'job-posting-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.getElementById('job-posting-schema')?.remove();
    };
  }, [job]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateAnswers = () => {
    if (!job.questions) return true;

    for (const question of job.questions) {
      if (question.required && (!answers[question.id] || answers[question.id].trim() === '')) {
        toast.error(`Please answer the required question: ${question.question}`);
        return false;
      }
    }
    return true;
  };

  const handleApply = async () => {
    if (!auth.currentUser) {
      toast.info('Please sign in to apply for this job');
      navigate('/candidate-sign-in');
      return;
    }

    try {
      // Check if user is an employer
      const employerRef = ref(db, `employers/${auth.currentUser.uid}`);
      const employerSnapshot = await get(employerRef);

      if (employerSnapshot.exists()) {
        toast.error('Employers cannot apply for jobs. Please use a candidate account.');
        return;
      }

      // Verify user is a candidate
      if (!isCandidate) {
        toast.error('Only candidates can apply for jobs. Please register as a candidate.');
        navigate("/candidate-register");
        return;
      }

      // Check if profile is complete
      const profileCheck = isProfileComplete(candidateData);
      if (!profileCheck.isComplete) {
        toast.error(`Please complete your profile before applying. Missing: ${profileCheck.missingFields.join(', ')}`);
        navigate('/candidate/profile');
        return;
      }

      // Check if profile is public (check both top-level and profile.isPublic for backwards compatibility)
      if (!(candidateData.isPublic || candidateData.profile?.isPublic)) {
        toast.error('Your profile must be set to Public to apply for jobs. Please update your profile settings.');
        navigate('/candidate/profile');
        return;
      }

      // Check if profile status is active (undefined means not set, treat as inactive)
      if (candidateData.profileStatus !== 'active' && candidateData.profileStatus !== 'completed') {
        toast.error('Your profile is inactive or expired. Please sign in again to reactivate your profile before applying.');
        navigate('/candidate/profile');
        return;
      }

      if (hasApplied) {
        toast.info('You have already applied for this job');
        return;
      }

      // Show confirmation modal before proceeding
      setIsConfirmModalOpen(true);
    } catch (error) {
      console.error('Error checking user type:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  const handleConfirmApplication = () => {
    setIsConfirmModalOpen(false);

    // Check if there are screening questions
    if (job.questions && job.questions.length > 0) {
      setIsApplyModalOpen(true);
    } else {
      submitApplication();
    }
  };

  const submitApplication = async () => {
    if (submittingRef.current) return;
    if (job.questions && job.questions.length > 0 && !validateAnswers()) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      // Double check employer status before submitting
      const employerRef = ref(db, `employers/${auth.currentUser.uid}`);
      const employerSnapshot = await get(employerRef);
      
      if (employerSnapshot.exists()) {
        toast.error('Employers cannot apply for jobs. Please use a candidate account.');
        return;
      }

      // Double check candidate status
      if (!isCandidate) {
        toast.error('Only candidates can apply for jobs. Please register as a candidate.');
        navigate("/candidate-register");
        return;
      }

      // Re-fetch candidate data to ensure profile is still valid at submission time
      const userId = auth.currentUser.uid;
      const freshCandidateRef = ref(db, `candidates/${userId}`);
      const freshCandidateSnapshot = await get(freshCandidateRef);

      if (!freshCandidateSnapshot.exists()) {
        toast.error('Candidate profile not found. Please register as a candidate.');
        navigate("/candidate-register");
        return;
      }

      const freshCandidateData = freshCandidateSnapshot.val();

      // Validate profile completeness
      const profileCheck = isProfileComplete(freshCandidateData);
      if (!profileCheck.isComplete) {
        toast.error(`Please complete your profile before applying. Missing: ${profileCheck.missingFields.join(', ')}`);
        navigate('/candidate/profile');
        return;
      }

      // Validate profile is public (check both top-level and profile.isPublic for backwards compatibility)
      if (!(freshCandidateData.isPublic || freshCandidateData.profile?.isPublic)) {
        toast.error('Your profile must be set to Public to apply for jobs.');
        navigate('/candidate/profile');
        return;
      }

      // Validate profile status is active (undefined means not set, treat as inactive)
      if (freshCandidateData.profileStatus !== 'active' && freshCandidateData.profileStatus !== 'completed') {
        toast.error('Your profile is inactive or expired. Please reactivate your profile before applying.');
        navigate('/candidate/profile');
        return;
      }

      const applicationRef = ref(db, `candidates/${userId}/applications/${job.id}`);

      const applicationData = {
        jobId: job.id,
        employerId: job.employerId,
        status: 'pending',
        appliedAt: Date.now(),
        jobTitle: job.jobTitle,
        companyName: job.companyName
      };

      if (job.questions && job.questions.length > 0) {
        applicationData.answers = answers;
      }

      await set(applicationRef, applicationData);

      const applicationId = `app-${Date.now()}-${userId.substring(0, 5)}`;

      // Create application in employer's jobs applications node
      const jobApplicationRef = ref(db, `jobs/${job.employerId}/${job.id}/applications/${userId}`);
      await set(jobApplicationRef, {
        candidateId: userId,
        applicationId: applicationId,
        status: 'pending',
        appliedAt: Date.now(),
        answers: answers
      });

      // Call the Cloud Function to send notification to employer
      try {
        const notifyCandidateApplication = httpsCallable(functions, 'notifyCandidateApplication');

        // Prepare data for the notification using fresh candidate data
        const notificationData = {
          employerId: job.employerId,
          jobId: job.id,
          jobTitle: job.jobTitle,
          applicationEmail: job.applicationEmail || '',
          createdByAdmin: job.createdByAdmin || false,
          candidateId: userId,
          candidateName: `${freshCandidateData.firstName || ''} ${freshCandidateData.lastName || ''}`.trim() || 'Candidate',
          applicationId: applicationId,
          summary: freshCandidateData.profile?.aboutMe || 'No summary provided',
          skills: freshCandidateData.profile?.skills || [],
          experience: freshCandidateData.profile?.workExperience || [],
          education: freshCandidateData.profile?.education || [],
          parish: freshCandidateData.parish || '',
          phone: freshCandidateData.phone || '',
          email: freshCandidateData.email || '',
          employmentType: freshCandidateData.employmentType || '',
          industriesOfInterest: freshCandidateData.industriesOfInterest || [],
          hasResume: !!freshCandidateData.profile?.resume?.url,
          hasPhoto: !!freshCandidateData.profile?.photo?.url
        };

        await notifyCandidateApplication(notificationData);
        console.log('Application notification sent to employer');
      } catch (notifyError) {
        console.error('Error sending application notification:', notifyError);
        // Don't fail the application process if notification fails
      }

      // Call the Cloud Function to send notification to admin
      try {
        const notifyAdminNewApplication = httpsCallable(functions, 'notifyAdminNewApplication');

        const adminNotificationData = {
          employerId: job.employerId,
          jobId: job.id,
          candidateId: userId,
          answers: answers
        };

        await notifyAdminNewApplication(adminNotificationData);
        console.log('Application notification sent to admin');
      } catch (adminNotifyError) {
        console.error('Error sending admin notification:', adminNotifyError);
        // Don't fail the application process if admin notification fails
      }

      // Update invitation status if this application came from an invitation
      try {
        const candidateInvRef = ref(db, `candidate-invitations/${userId}`);
        const candidateInvSnapshot = await get(candidateInvRef);

        if (candidateInvSnapshot.exists()) {
          const invitations = candidateInvSnapshot.val();
          const matchingKey = Object.keys(invitations).find(key =>
            invitations[key].jobId === job.id && invitations[key].status === 'pending'
          );

          if (matchingKey) {
            const appliedUpdate = { status: 'applied', appliedAt: new Date().toISOString() };

            // Update candidate's invitation record
            await update(ref(db, `candidate-invitations/${userId}/${matchingKey}`), appliedUpdate);

            // Update employer's invitation record directly using stored key
            // (candidate lacks read access to employer's invitations list)
            const employerInvitationKey = invitations[matchingKey].employerInvitationKey;
            if (employerInvitationKey) {
              await update(ref(db, `invitations/${job.employerId}/${employerInvitationKey}`), appliedUpdate);
            }
          }
        }
      } catch (invError) {
        console.error('Error updating invitation status:', invError);
      }

      setHasApplied(true);
      setIsApplyModalOpen(false);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleFavorite = async () => {
    if (!auth.currentUser) {
      toast.info('Please sign in to save jobs');
      navigate('/candidate-sign-in');
      return;
    }

    if (!isCandidate) {
      toast.error('Only candidates can save jobs');
      return;
    }

    try {
      const userId = auth.currentUser.uid;
      const favoriteRef = ref(db, `candidates/${userId}/favorites/${job.id}`);

      if (isFavorite) {
        await set(favoriteRef, null);
        setIsFavorite(false);
        toast.success('Job removed from favorites');
      } else {
        await set(favoriteRef, {
          jobId: job.id,
          employerId: job.employerId,
          savedAt: Date.now(),
          jobTitle: job.jobTitle,
          companyName: job.companyName
        });
        setIsFavorite(true);
        toast.success('Job saved to favorites!');
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      toast.error('Failed to update favorites. Please try again.');
    }
  };

  const ConfirmApplicationModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setIsConfirmModalOpen(false)}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <FaQuestionCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Confirm Application
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to apply to this job?
                  </p>
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-900">{job.jobTitle}</p>
                    <p className="text-sm text-gray-600">{job.companyName}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirmApplication}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Yes, Apply
            </button>
            <button
              type="button"
              onClick={() => setIsConfirmModalOpen(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ApplicationQuestionsModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
  
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <FaQuestionCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Application Questions
                </h3>
                <div className="mt-4 space-y-4">
                  {job.questions.map((question) => (
                    <div key={question.id} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {question.question}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      
                      {question.type === 'multiple_choice' ? (
                        <select
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          required={question.required}
                        >
                          <option value="">Select an option</option>
                          {question.options?.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : question.type === 'yes_no' ? (
                        <div className="flex space-x-4">
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name={`question_${question.id}`}
                              value="Yes"
                              checked={answers[question.id] === 'Yes'}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              className="form-radio h-4 w-4 text-blue-600"
                            />
                            <span className="ml-2">Yes</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name={`question_${question.id}`}
                              value="No"
                              checked={answers[question.id] === 'No'}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              className="form-radio h-4 w-4 text-blue-600"
                            />
                            <span className="ml-2">No</span>
                          </label>
                        </div>
                      ) : (
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-y min-h-[100px]"
                          required={question.required}
                          placeholder="Enter your answer here..."
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                            lineHeight: '1.5'
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={submitApplication}
              disabled={isSubmitting}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
            <button
              type="button"
              onClick={() => setIsApplyModalOpen(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  if (error) {
    if (error === 'Job not found') return <NotFound />;
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!job) return null;

  const pageTitle = `${job.jobTitle} - ${job.companyName} | ${job.parish || 'Jamaica'} | HireMeJA`;
  const pageDescription = `${job.jobTitle} at ${job.companyName} in ${job.parish || 'Jamaica'}. ${job.employmentType ? job.employmentType + '. ' : ''}${(job.description || '').replace(/<[^>]+>/g, '').substring(0, 150)}...`;
  const pageUrl = `https://hiremeja.com/jobs/${job.slug || job.id}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {job.companyLogo && <meta property="og:image" content={job.companyLogo} />}
      </Helmet>
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Expired Job Notice */}
        {isExpiredJob && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <FaClock className="text-red-500 mr-3 flex-shrink-0" size={20} />
              <div>
                <h3 className="text-red-800 font-semibold">This Job Listing Has Expired</h3>
                <p className="text-red-700 text-sm mt-1">
                  This position is no longer accepting new applications. You can still view the job details below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Company Header Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="col-span-2">
              {/* Logo and Basic Info */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                  {job.companyLogo ? (
                    <img src={job.companyLogo} alt={job.companyName} className="w-full h-full object-cover rounded-lg"/>
                  ) : (
                    <FaBuilding className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-blue-950">{job.jobTitle}</h1>
                  <p className="text-lg text-gray-700">{job.companyName}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {job.employmentType}
                    </span>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                      {job.experience}
                    </span>
                  </div>
                </div>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-gray-500" />
                  <span>{job.parish}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaBriefcase className="text-gray-500" />
                  <span>{job.employmentType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaLaptopHouse className="text-gray-500" />
                  <span>{job.workType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaDollarSign className="text-gray-500" />
                  <span>{job.salary}</span>
                </div>
                {job.questions && job.questions.length > 0 && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <FaQuestionCircle className="text-gray-500" />
                    <span>{job.questions.length} Screening {job.questions.length === 1 ? 'Question' : 'Questions'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons Column */}
            <div className="flex flex-col gap-3 justify-center">
              {(() => {
                const profileCheck = isCandidate && candidateData ? isProfileComplete(candidateData) : null;
                const profileIncomplete = isCandidate && profileCheck && !profileCheck.isComplete;
                const profileNotPublic = isCandidate && candidateData && !(candidateData.isPublic || candidateData.profile?.isPublic);
                const profileInactive = isCandidate && candidateData && candidateData.profileStatus !== 'active' && candidateData.profileStatus !== 'completed';
                const cannotApply = isExpiredJob || hasApplied || !isCandidate || profileIncomplete || profileNotPublic || profileInactive;

                return (
                  <>
                    <button
                      onClick={handleApply}
                      disabled={cannotApply}
                      className={`w-full py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2
                        ${hasApplied
                          ? 'bg-green-100 text-green-800 cursor-not-allowed'
                          : isExpiredJob
                          ? 'bg-red-100 text-red-600 cursor-not-allowed'
                          : cannotApply
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-950 text-white hover:bg-[#cddd3a] hover:text-blue-950'
                        }`}
                    >
                      {hasApplied ? 'Already Applied' : isExpiredJob ? 'Job Expired' : 'Apply Now'}
                    </button>
                    {isExpiredJob && (
                      <p className="text-xs text-red-600 text-center">
                        This job listing has expired and is no longer accepting applications.
                      </p>
                    )}
                    {profileIncomplete && (
                      <p className="text-xs text-red-600 text-center">
                        Complete your profile to apply. Missing: {profileCheck.missingFields.join(', ')}
                      </p>
                    )}
                    {profileNotPublic && !profileIncomplete && (
                      <p className="text-xs text-red-600 text-center">
                        Set your profile to Public to apply for jobs
                      </p>
                    )}
                    {profileInactive && !profileIncomplete && !profileNotPublic && (
                      <p className="text-xs text-red-600 text-center">
                        Your profile is inactive. Sign in to reactivate it.
                      </p>
                    )}
                  </>
                );
              })()}
              <button
                onClick={handleFavorite}
                disabled={!isCandidate}
                className={`w-full py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2
                  ${!isCandidate
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : isFavorite
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <FaHeart className={isFavorite ? 'fill-current' : 'regular'} />
                {isFavorite ? 'Saved' : 'Save Job'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Job Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Job Description */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-blue-950 mb-4">Job Description</h2>
              <div className="prose max-w-none">
                <p className="whitespace-pre-line text-gray-700">{job.description}</p>
              </div>
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-blue-950 mb-4">Requirements</h2>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-line text-gray-700">{job.requirements}</p>
                </div>
              </div>
            )}

            {/* Other Requirements */}
            {job.otherRequirements && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-blue-950 mb-4">Other Requirements</h2>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-line text-gray-700">{job.otherRequirements}</p>
                </div>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-blue-950 mb-4">Benefits</h2>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-line text-gray-700">{job.benefits}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Additional Info */}
          <div className="space-y-6">
            {/* Job Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-bold text-blue-950 mb-4">Job Overview</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FaCalendarAlt className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Posted Date</p>
                    <p className="text-gray-600">
                      {new Date(job.approvedAt || job.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {(job.expirationDate || job.expiryDate) && (
                  <div className="flex items-start gap-3">
                    <FaCalendarAlt className="text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm font-medium">Expires</p>
                      <p className="text-gray-600">
                        {new Date(job.expirationDate || job.expiryDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <FaIndustry className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Industry</p>
                    <p className="text-gray-600">{job.industry}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaGraduationCap className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Education Level</p>
                    <p className="text-gray-600">{job.degreeLevel}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaClock className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Experience</p>
                    <p className="text-gray-600">{job.experience}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaLaptopHouse className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Work Type</p>
                    <p className="text-gray-600">{job.workType}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Contact Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-bold text-blue-950 mb-4">Contact Information</h2>
              <div className="space-y-4">
                {job.contactEmail && (
                  <div className="flex items-start gap-3">
                    <FaEnvelope className="text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm font-medium">Email</p>
                      <a href={`mailto:${job.contactEmail}`} className="text-blue-600 hover:underline">
                        {job.contactEmail}
                      </a>
                    </div>
                  </div>
                )}

                {job.contactPhone && (
                  <div className="flex items-start gap-3">
                    <FaPhone className="text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm font-medium">Phone</p>
                      <p className="text-gray-600">{job.contactPhone}</p>
                    </div>
                  </div>
                )}

                {job.website && (
                  <div className="flex items-start gap-3">
                    <FaGlobe className="text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm font-medium">Website</p>
                      <a 
                        href={job.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {job.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Application Instructions */}
        {job.applicationInstructions && (
          <div className="mt-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-blue-950 mb-4">How to Apply</h2>
              <div className="prose max-w-none">
                <p className="whitespace-pre-line text-gray-700">
                  {job.applicationInstructions}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Job Post Info */}
        <div className="mt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 text-center">
              Posted on{' '}
              {new Date(job.approvedAt || job.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              {(job.expirationDate || job.expiryDate) && ` • Expires on ${new Date(job.expirationDate || job.expiryDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}`}
            </p>
          </div>
        </div>

        {/* Share Job Section */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 mb-2">Know someone who would be perfect for this role?</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <button 
              onClick={() => {
                const subject = encodeURIComponent(`Job Opportunity: ${job.jobTitle} at ${job.companyName}`);
                const body = encodeURIComponent(
                  `Hi,\n\nI found this job opportunity that might interest you:\n\n` +
                  `Position: ${job.jobTitle}\n` +
                  `Company: ${job.companyName}\n` +
                  `Location: ${job.parish}\n` +
                  `Employment Type: ${job.employmentType}\n` +
                  `Experience Required: ${job.experience}\n` +
                  `Salary: ${job.salary}\n\n` +
                  `Job Description:\n${job.description.substring(0, 200)}${job.description.length > 200 ? '...' : ''}\n\n` +
                  `View full job details and apply here: ${window.location.href}\n\n` +
                  `Best regards`
                );
                window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <FaEnvelope />
              Share via Email
            </button>
            
            <button 
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: `${job.jobTitle} at ${job.companyName}`,
                    text: `Check out this job opportunity: ${job.jobTitle} at ${job.companyName}`,
                    url: window.location.href
                  }).catch(console.error);
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Job link copied to clipboard!');
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              Share Link
            </button>
          </div>
        </div>

        {/* Back to Jobs Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/jobs')}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to All Jobs
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && <ConfirmApplicationModal />}

      {/* Application Questions Modal */}
      {isApplyModalOpen && <ApplicationQuestionsModal />}
    </div>
    </>
  );
};

export default JobDetails;